/**
 * A collapsible container that shows/hides content when the header is clicked
 * Includes an arrow indicator that rotates based on open state
 */
import styleText from './collapse-content.css?raw';

export class CollapseContent extends HTMLElement {
  // Shadow DOM elements
  private defaultHeaderText: HTMLDivElement | undefined;
  private static readonly sheet = (() => {
    const sheet = new CSSStyleSheet();
    sheet.replaceSync(styleText);
    return sheet;
  })();
  
  // Observed attributes
  static get observedAttributes(): string[] {
    return ['open', 'header-text'];
  }
  
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.shadowRoot!.adoptedStyleSheets = [
      ...this.shadowRoot!.adoptedStyleSheets,
      CollapseContent.sheet
    ];
    this.render();
    this.setupEventListeners();
  }
  
  /**
   * Renders the component structure and styles using HTML templates
   */
  private render(): void {
    if (!this.shadowRoot) return;
    
    // Create template
    const template = document.createElement('template');
    
    // Set default header text
    const headerText = this.getAttribute('header-text') ?? 'Collapsible Section';
    
    // Define the component HTML structure with styles
    template.innerHTML = `
      <div class="container">
        <div class="header">
          <div class="default-header-text">${headerText}</div>
          <slot name="header"></slot>
          <span class="arrow"></span>
        </div>
        <div class="content">
          <slot></slot>
        </div>
      </div>
    `;
    
    // Clone and append the template content
    this.shadowRoot.appendChild(template.content.cloneNode(true));
    
    // Store reference to default header text for later updates
    this.defaultHeaderText = this.shadowRoot.querySelector('.default-header-text');
    
    // Check if header slot has content
    this.checkHeaderSlot();
  }
  
  /**
   * Sets up event listeners for the component
   */
  private setupEventListeners(): void {
    if (!this.shadowRoot) return;
    
    // Add click listener to header
    const header = this.shadowRoot.querySelector('.header');
    header?.addEventListener('click', () => {
      this.toggleOpen();
    });
    
    // Listen for slot changes to show/hide default header
    const headerSlot = this.shadowRoot.querySelector('slot[name="header"]');
    headerSlot?.addEventListener('slotchange', () => {
      this.checkHeaderSlot();
    });
  }
  
  /**
   * Checks if the header slot has content and shows/hides default header accordingly
   */
  private checkHeaderSlot(): void {
    if (!this.shadowRoot) return;
    
    const headerSlot = this.shadowRoot.querySelector('slot[name="header"]') as HTMLSlotElement;
    if (headerSlot) {
      const hasContent = headerSlot.assignedNodes().length > 0;
      if (this.defaultHeaderText) {
        this.defaultHeaderText.style.display = hasContent ? 'none' : 'block';
      }
    }
  }
  
  /**
   * Toggles the open state of the collapsible content
   */
  public toggleOpen(): void {
    const isOpen = this.hasAttribute('open');
    if (isOpen) {
      this.removeAttribute('open');
    } else {
      this.setAttribute('open', '');
    }
  }
  
  /**
   * Handles changes to observed attributes
   */
  attributeChangedCallback(name: string, oldValue: string, newValue: string): void {
    if (name === 'open' && oldValue !== newValue) {
      // Dispatch event when open state changes
      const event = new CustomEvent('toggle', {
        detail: { open: this.hasAttribute('open') },
        bubbles: true
      });
      this.dispatchEvent(event);
    } else if (name === 'header-text' && this.defaultHeaderText) {
      // Update default header text when attribute changes
      this.defaultHeaderText.textContent = newValue ?? 'Collapsible Section';
    }
  }
  
  /**
   * Sets the open state programmatically
   */
  set open(value: boolean) {
    if (value) {
      this.setAttribute('open', '');
    } else {
      this.removeAttribute('open');
    }
  }
  
  /**
   * Gets the current open state
   */
  get open(): boolean {
    return this.hasAttribute('open');
  }
  
  /**
   * Sets the header text programmatically
   */
  set headerText(value: string) {
    this.setAttribute('header-text', value);
  }
  
  /**
   * Gets the current header text
   */
  get headerText(): string {
    return this.getAttribute('header-text') ?? 'Show / Hide';
  }
}

// Check if the element is already defined before registering it
if (!customElements.get('collapse-content')) {
  customElements.define('collapse-content', CollapseContent);
} else {
  console.warn("collapse-content already defined");
}
